import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Info, X, Upload as UploadIcon, FileText, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api, request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { Modal, Select, Input, Textarea, Button, Tag, AutoComplete, EntityAvatar, CodeChip, entityHue, cn, toast, type TagTone } from '../../ui';
import { ProductThumb } from '../../components/ProductThumb';
import { accentForProductName } from '../../lib/productAccent';
import { inr2 } from '../../lib/format';
import { usePermission } from '../../store/auth';

const MAX_PROOFS = 6;
const isImage = (u: string) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);

/**
 * Raise a sales-return REQUEST against an ORDER (Phase 1 of the return lifecycle). Tailwind/ui-kit modal (no
 * antd). The retailer picks product(s) from a dropdown that shows how many units of each were DELIVERED, and
 * the return qty is capped at what was delivered (∩ billed) — you can only return goods you actually received,
 * so an undelivered-but-billed remainder is settled by short-close, not a return. No stock/money moves; Admin/SA
 * approve next, the warehouse receives + inspects, and only then does Accounts issue the credit note(s).
 *
 * THE CLAIM IS RECORDED PER PRODUCT, BUT ONLY ASKED FOR WHERE THE ANSWER IS UNKNOWN.
 *
 * The record needs the fault and the lot on the LINE: one reason for the whole document could not say which
 * product came back damaged and which was merely over-ordered, and the Accounts step read that single code as
 * the fault for every line — suggesting the same credit for goods the seller broke and goods the buyer changed
 * their mind about. So every line now stores its own reason, lot and note.
 *
 * That is a reason to change the DATA, not automatically the FORM. Each control here earns its place only when
 * the raiser knows something the system does not:
 *   • the reason per product appears once there is more than one product — with one, the return's reason IS
 *     that product's reason, and asking twice is the same question twice;
 *   • the lot appears once the product went out in more than one — with a single delivered lot, dispatch
 *     already recorded which, and it is stored from there without anyone retyping it.
 * A one-product, one-lot return therefore asks exactly what it always asked: product, quantity, reason, proof.
 *
 * Where the lot IS asked, free entry stays open — a lot the system never recorded sending must not block a
 * genuine return (the dispatch record may itself be wrong) — and is flagged on the record instead.
 */

/*
 * Fault-categorized reasons (mirror the backend RETURN_REASON_CODES). Seller-fault → full credit later.
 *
 * Each carries the SAME tone it wears on the return record, so a reason is the one colour everywhere it is
 * read — picked here, approved on the record, paid by Accounts. A reason that changed hue between screens
 * would make the reader re-learn it at every step.
 */
const REASON_OPTIONS = [
  { value: 'DAMAGED_IN_TRANSIT', label: 'Damaged in transit', fault: 'seller', tone: 'red' },
  { value: 'DEFECTIVE', label: 'Defective / quality issue', fault: 'seller', tone: 'red' },
  { value: 'WRONG_ITEM', label: 'Wrong item / short-shipped', fault: 'seller', tone: 'gold' },
  { value: 'EXPIRED_ON_DELIVERY', label: 'Expired on delivery', fault: 'seller', tone: 'red' },
  { value: 'NOT_NEEDED', label: 'Not needed / over-ordered', fault: 'buyer', tone: 'blue' },
  { value: 'ORDERED_BY_MISTAKE', label: 'Ordered by mistake', fault: 'buyer', tone: 'blue' },
  { value: 'CHANGED_MIND', label: 'Changed mind', fault: 'buyer', tone: 'blue' },
  { value: 'OTHER', label: 'Other', fault: 'buyer', tone: 'neutral' },
] as const;

/** One reason, as it reads in a dropdown row: its own colour, then who the fault falls on. */
function ReasonOptionLabel({ o }: { o: (typeof REASON_OPTIONS)[number] }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Tag tone={o.tone as TagTone}>{o.label}</Tag>
      <span className="type-caption text-muted">{o.fault === 'seller' ? 'seller fault' : 'buyer choice'}</span>
    </span>
  );
}

/** A figure in the band across the top: what this return currently adds up to. */
function Stat({ label: lbl, value, hint, tint }: { label: string; value: React.ReactNode; hint?: string; tint: string }) {
  return (
    <div className={cn('min-w-0 flex-1 rounded-card border border-line p-2.5 shadow-e1', tint)}>
      <div className="type-label text-muted">{lbl}</div>
      <div className="mt-0.5 type-metric wrap-break-word text-content">{value}</div>
      {hint && <div className="type-caption text-subtle">{hint}</div>}
    </div>
  );
}

/**
 * Which lot is coming back. The list is what this order actually DELIVERED for that product, with how many
 * units of each — so the ordinary answer is one click, and picking it links the claim to the real batch.
 *
 * Free entry is kept on purpose. A lot the system never recorded sending is not a reason to refuse the return:
 * the dispatch record may itself be wrong, or the retailer may genuinely be holding stock from elsewhere. It is
 * said out loud instead, here and on the record, so the approver and the warehouse know before they act.
 */
function ClaimBatchInput({ lots, value, onChange }: {
  lots: { batchId: string; batchNo: string; expiry: string | null; qty: number }[];
  value: string; onChange: (v: string) => void;
}) {
  const exp = (e: string | null) => (e ? ` · exp ${String(e).slice(0, 10)}` : '');
  const options = lots.map((b) => ({
    value: b.batchNo,
    label: <span>{b.batchNo}<span className="text-subtle">{exp(b.expiry)} · {fmtQty(b.qty)} delivered</span></span>,
  }));
  const typed = value.trim();
  // Only call it unknown when we know what was delivered — with nothing on record there is nothing to contradict.
  const unknown = lots.length > 0 && !!typed && !lots.some((b) => b.batchNo.toLowerCase() === typed.toLowerCase());
  return (
    <div className="min-w-0">
      <AutoComplete
        style={{ width: '100%' }}
        options={options}
        value={value}
        onChange={onChange}
        allowClear
        placeholder={lots.length ? 'Pick or type the lot' : 'Type the lot off the pack'}
        filterOption={(input: string, opt: any) => String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
      />
      {unknown && (
        <div className="mt-1 type-label text-warning-600 dark:text-harvest-300">
          ⚠ not a lot delivered to you on this order — it will be flagged for the warehouse
        </div>
      )}
    </div>
  );
}

export function RaiseReturnModal({ open, onClose, order, invoices, shipments }: {
  open: boolean; onClose: () => void; order: any; invoices: any[]; shipments: any[];
}) {
  const qc = useQueryClient();
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [shown, setShown] = useState<string[]>([]); // orderItemIds the retailer added to the return
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  // Per product, keyed by orderItemId: the fault claimed for it, the lot it came back in, and what happened to
  // it in the raiser's words. `reasonTouched` remembers which products the raiser set deliberately, so changing
  // the default afterwards re-seeds the rest without overwriting a choice they already made.
  const [lineReason, setLineReason] = useState<Record<string, string>>({});
  const [lineBatch, setLineBatch] = useState<Record<string, string>>({});
  const [lineNote, setLineNote] = useState<Record<string, string>>({});
  const [reasonTouched, setReasonTouched] = useState<Record<string, boolean>>({});
  const [proofs, setProofs] = useState<string[]>([]); // uploaded proof file URLs (mandatory)
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQtys({}); setShown([]); setReasonCode(''); setNote(''); setProofs([]);
      setLineReason({}); setLineBatch({}); setLineNote({}); setReasonTouched({});
    }
  }, [open]);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const f of Array.from(files).slice(0, MAX_PROOFS - proofs.length)) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`"${f.name}" is too large — max 25 MB`); continue; }
      setUploading((u) => u + 1);
      try {
        const fd = new FormData(); fd.append('file', f);
        const r = await api.post('/uploads/evidence', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = (r.data?.data ?? r.data)?.url;
        if (url) setProofs((p) => (p.length < MAX_PROOFS ? [...p, url] : p));
      } catch (e: any) {
        toast.error(e.response?.data?.error?.message ?? e.message ?? 'Upload failed');
      } finally {
        setUploading((u) => u - 1);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // BILLED per line — summed across every LIVE invoice (per-shipment invoice bills its shipment; whole-order once).
  const billedByItem = useMemo(() => {
    const m = new Map<string, number>();
    const add = (oid: string, q: number) => m.set(oid, (m.get(oid) ?? 0) + q);
    const liveInvoices = (invoices ?? []).filter((i: any) => i.status !== 'CANCELLED');
    let countedWhole = false;
    for (const inv of liveInvoices) {
      if (inv.shipmentId) {
        const ship = (shipments ?? []).find((s: any) => String(s.id) === String(inv.shipmentId));
        for (const it of (ship?.items ?? [])) add(String(it.orderItemId), Number(it.qty || 0));
      } else if (!countedWhole) {
        for (const oi of (order?.items ?? [])) add(String(oi.id), Math.max(0, Number(oi.qty) - Number(oi.cancelledQty ?? 0)));
        countedWhole = true;
      }
    }
    return m;
  }, [invoices, shipments, order]);

  // DELIVERED per line = goods that physically reached the retailer (DELIVERED shipments only).
  const deliveredByItem = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of (shipments ?? [])) {
      if (String(s.status) !== 'DELIVERED') continue;
      for (const it of (s.items ?? [])) m.set(String(it.orderItemId), (m.get(String(it.orderItemId)) ?? 0) + Number(it.qty || 0));
    }
    return m;
  }, [shipments]);

  // The LOT(S) each product was delivered in — read off the delivered shipments, which is exactly the stock the
  // retailer is holding. This is what the batch picker offers, and what the warehouse later cross-checks the
  // returned goods against. A shipment packed before batch allocation has none, and the raiser types the lot
  // off the pack instead.
  const dispatchedByItem = useMemo(() => {
    const m = new Map<string, { batchId: string; batchNo: string; expiry: string | null; qty: number }[]>();
    for (const s of (shipments ?? [])) {
      if (String(s.status) !== 'DELIVERED') continue;
      for (const it of (s.items ?? [])) {
        if (!it.batchNo) continue;
        const key = String(it.orderItemId);
        const arr = m.get(key) ?? [];
        const ex = arr.find((b) => b.batchNo === String(it.batchNo));
        if (ex) ex.qty += Number(it.qty || 0);
        else arr.push({ batchId: String(it.batchId ?? ''), batchNo: String(it.batchNo), expiry: it.expiry ?? null, qty: Number(it.qty || 0) });
        m.set(key, arr);
      }
    }
    return m;
  }, [shipments]);

  // Returnable = products with something delivered; cap = min(billed, delivered). Backend enforces the true cap.
  const returnable = useMemo(() => (order?.items ?? [])
    .map((oi: any) => {
      const billed = billedByItem.get(String(oi.id)) ?? 0;
      const delivered = deliveredByItem.get(String(oi.id)) ?? 0;
      const name = `${oi.productNameSnapshot ?? ''}${oi.variantLabel ? ` ${oi.variantLabel}` : ''}`.trim() || `#${oi.productId}`;
      // The per-unit rate the return will actually be valued at — taxable ÷ ordered qty, the SAME formula the
      // server uses to price a return line, so the estimate shown here is the figure that gets recorded rather
      // than a second opinion computed from the list price.
      const ordered = Number(oi.qty) || 0;
      const rate = ordered > 0 ? Number(oi.taxableAmt ?? 0) / ordered : 0;
      return { orderItemId: String(oi.id), name, delivered, cap: Math.min(billed, delivered), rate };
    })
    .filter((l: any) => l.cap > 0), [order, billedByItem, deliveredByItem]);
  const byId = useMemo(() => new Map(returnable.map((l: any) => [l.orderItemId, l])), [returnable]);

  const shownLines = shown.map((id) => byId.get(id)).filter(Boolean) as any[];
  const addOptions = returnable
    .filter((l: any) => !shown.includes(l.orderItemId))
    .map((l: any) => ({ value: l.orderItemId, label: `${l.name} · ${fmtQty(l.delivered)} delivered` }));

  const addProduct = (id: string) => {
    setShown((s) => (s.includes(id) ? s : [...s, id]));
    setQtys((m) => ({ ...m, [id]: (byId.get(id) as any)?.cap ?? 0 })); // default to the full delivered qty
    // Seed the claim: the reason defaults to the one chosen for the return, and the lot pre-fills when the
    // product went out in exactly one — the ordinary case, and the raiser only corrects it if it is wrong.
    setLineReason((m) => ({ ...m, [id]: m[id] ?? reasonCode }));
    setLineBatch((m) => {
      const lots = dispatchedByItem.get(id) ?? [];
      return { ...m, [id]: m[id] ?? (lots.length === 1 ? lots[0].batchNo : '') };
    });
  };
  const removeProduct = (id: string) => {
    setShown((s) => s.filter((x) => x !== id));
    const drop = (m: Record<string, any>) => { const n = { ...m }; delete n[id]; return n; };
    setQtys(drop); setLineReason(drop); setLineBatch(drop); setLineNote(drop); setReasonTouched(drop);
  };
  const setQty = (id: string, v: number, cap: number) =>
    setQtys((m) => ({ ...m, [id]: Math.max(0, Math.min(Number.isFinite(v) ? v : 0, cap)) }));

  // The reason at the top is a DEFAULT, not an override: it re-seeds every product the raiser has not set
  // themselves, and leaves the ones they have alone.
  const setDefaultReason = (code: string) => {
    setReasonCode(code);
    setLineReason((m) => {
      const n = { ...m };
      for (const id of shown) if (!reasonTouched[id]) n[id] = code;
      return n;
    });
  };
  const setProductReason = (id: string, code: string) => {
    setLineReason((m) => ({ ...m, [id]: code }));
    setReasonTouched((m) => ({ ...m, [id]: true }));
  };

  const total = shown.reduce((s, id) => s + Number(qtys[id] || 0), 0);
  const claimed = shown.filter((id) => Number(qtys[id]) > 0);

  /*
   * ONLY ASK WHAT NOBODY ALREADY KNOWS.
   *
   * The per-product claim is what the record needs; it is not automatically what the FORM needs. Two of the
   * three facts are usually already settled by the time the raiser opens this modal:
   *
   *   • the reason — on a ONE-product return the return's reason IS that product's reason. Asking again is
   *     the same question twice, and every return raised so far has had exactly one product.
   *   • the lot — when the product went out in a single lot, dispatch already recorded which one. Making
   *     someone retype it is data entry, not information.
   *
   * So each control appears only where the answer is genuinely unknown: reasons per product once there is
   * more than one product, and the lot once the product went out in more than one. A single-product,
   * single-lot return — the ordinary one — asks for nothing it did not ask for before.
   */
  const multiProduct = claimed.length > 1;
  const lotsFor = (id: string) => dispatchedByItem.get(id) ?? [];
  const lotIsAQuestion = (id: string) => lotsFor(id).length > 1;

  const missingReason = multiProduct && claimed.some((id) => !lineReason[id]);
  const missingBatch = claimed.some((id) => lotIsAQuestion(id) && !(lineBatch[id] ?? '').trim());
  const canSubmit = total > 0 && !!reasonCode && !missingReason && !missingBatch && proofs.length > 0 && uploading === 0;

  // WHAT THIS RETURN IS WORTH, while it is still being written. The form used to be submitted blind: the
  // claim value only appeared on the record afterwards, so nobody raising a return could see whether they
  // were asking for ₹200 or ₹20,000 until it was already in the approver's queue. Pre-tax, because the real
  // GST is worked out per invoice at the Accounts step and a tax figure here would be a guess.
  const estValue = claimed.reduce((s, id) => s + Number(qtys[id] || 0) * ((byId.get(id) as any)?.rate ?? 0), 0);

  // Mirrors the server rule: a raiser who can approve gets the return approved in the same act.
  const canApprove = usePermission('RETURN.APPROVE');

  const send = useConfirmedMutation({ title: "Raise this return?", content: "It goes into the returns queue. A credit note is only issued once the goods are received back.", okText: "Yes, raise it" }, {
    mutationFn: () => request({
      url: '/returns/request', method: 'POST',
      data: {
        orderId: String(order?.id), returnDate: dayjs().format('YYYY-MM-DD'),
        reasonCode, reasonNote: note.trim() || undefined, proofUrls: proofs,
        lines: claimed.map((id) => {
          const lot = (lineBatch[id] ?? '').trim();
          // Send the lot's id too when it is one this order delivered, so the claim links to the real batch
          // rather than to a string. A lot typed by hand that we never sent travels as the number alone.
          const hit = (dispatchedByItem.get(id) ?? []).find((b) => b.batchNo.toLowerCase() === lot.toLowerCase());
          return {
            orderItemId: id, qty: Number(qtys[id]),
            reasonCode: lineReason[id] || reasonCode,
            reasonNote: (lineNote[id] ?? '').trim() || undefined,
            batchNo: lot || undefined,
            batchId: hit?.batchId || undefined,
          };
        }),
      },
    }),
    onSuccess: (r: any) => {
      // An approver raising a return has nobody to wait for, so don't tell them to wait.
      toast.success(r?.autoApproved
        ? `Return ${r?.return?.returnNo ?? ''} raised and approved — the warehouse can receive the goods`
        : `Return ${r?.return?.returnNo ?? ''} raised — awaiting approval`);
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['order', String(order?.id)] });
      qc.invalidateQueries({ queryKey: ['order-timeline', String(order?.id)] });
      onClose();
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Failed to raise the return'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={order ? `Raise a return — ${order.orderNo}` : 'Raise a return'}
      width={680}
      footer={
        <>
          {/* A disabled button that will not say why is a dead end — name the one thing still missing. */}
          {!canSubmit && total > 0 && (
            <span className="mr-auto type-caption text-warning-600 dark:text-harvest-300">
              {missingReason ? 'Every product needs a reason'
                : missingBatch ? 'Every product needs the lot it is coming back in'
                : proofs.length === 0 ? 'Attach at least one photo as proof'
                : uploading > 0 ? 'Waiting for the upload to finish'
                : 'Choose a reason for the return'}
            </span>
          )}
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canSubmit} loading={send.isPending} onClick={() => canSubmit && send.mutate()}>
            {total > 0 ? `Raise return — ${fmtQty(total)} unit${total === 1 ? '' : 's'}` : 'Raise return'}
          </Button>
        </>
      }
    >
      {returnable.length === 0 ? (
        <div className="rounded-control border border-warning-500/30 bg-warning-500/10 px-3.5 py-3 text-sm text-warning-700 dark:text-warning-300">
          Nothing on this order has been delivered to you yet — you can only return goods you've actually received.
        </div>
      ) : (
        <div className="space-y-4">
          {/* WHO THIS IS FOR. A return is raised against a dealer's goods, and the form named the order but
              never the customer — the one fact that decides whether you are on the right screen at all. */}
          <div className="flex flex-wrap items-center gap-2 rounded-card border border-line bg-brand-500/7 px-3 py-2.5 shadow-e1">
            <EntityAvatar name={order?.companyName ?? order?.retailerName ?? String(order?.retailerId ?? '?')} size={30} />
            <div className="min-w-0">
              <div className="type-label text-muted">Returning from</div>
              <div className="type-body-strong wrap-break-word text-content">{order?.companyName ?? order?.retailerName ?? `#${order?.retailerId ?? '?'}`}</div>
            </div>
            {order?.retailerCode && <CodeChip hue={entityHue(order.retailerCode)}>{order.retailerCode}</CodeChip>}
          </div>

          {/* What happens */}
          <div className="flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-sm text-content">
            <Info size={16} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
            <span>
              {canApprove
                ? 'You can approve returns, so this one is approved as you raise it — recorded as approved by you. The warehouse then receives + inspects the goods and Accounts issues the credit note; no stock or money moves yet.'
                : 'This raises a return request. An admin approves it, the warehouse receives + inspects the goods, then Accounts issues the credit note — no stock or money moves yet.'}
            </span>
          </div>

          {/* WHAT IT ADDS UP TO, as it is written. Each figure names its own unit and appears exactly once on
              the form — the footer button no longer repeats the unit count. */}
          {claimed.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Stat label="Products" value={claimed.length} tint="bg-brand-500/7" />
              {/* The unit count lives on the submit button, which is pinned and therefore readable from
                  anywhere in a long form — so it is not repeated here. */}
              <Stat label="Claim value" value={inr2(estValue)} hint="pre-tax, at the billed rate" tint="bg-success-500/7" />
            </div>
          )}

          {/* The default every product inherits. Sits ABOVE the picker because it is the answer for the
              ordinary return — one reason, several products — and each card can still differ from it. */}
          <div>
            <label className="mb-1.5 block type-label text-content">Reason for return <span className="text-danger-500">*</span></label>
            <Select
              value={reasonCode || undefined}
              onChange={setDefaultReason}
              placeholder="Why is it being returned?"
              className="w-full"
              options={REASON_OPTIONS.map((o) => ({ value: o.value, label: <ReasonOptionLabel o={o} /> }))}
            />
            {multiProduct && (
              <div className="mt-1 text-2xs text-subtle">Each product starts from this and can be changed below — a return can carry more than one reason.</div>
            )}
          </div>

          {/* Product picker */}
          <div>
            <label className="mb-1.5 block text-xs text-muted">Pick the product(s) to return — you can return up to the quantity delivered to you.</label>
            <Select
              key={`add-${shown.length}`}   // remount after each add so the trigger resets to the placeholder
              value={undefined}
              onChange={(id) => id && addProduct(id)}
              options={addOptions}
              placeholder={addOptions.length ? 'Select a product to return…' : 'All delivered products added'}
              disabled={!addOptions.length || !reasonCode}
              className="w-full"
            />
            {!reasonCode && <div className="mt-1 text-2xs text-subtle">Choose a reason first — it is what each product you add starts from.</div>}
          </div>

          {/* What is being claimed, product by product. A card carries qty always; the lot and the reason join
              it only when they are real questions (see the note on `multiProduct` above), so the ordinary
              one-product return still reads as a single row of facts.

              The card wears the FAULT as its colour — amber where the seller owes a full credit, blue where
              the return is the buyer's own choice — because that distinction is what the whole document is
              finally about, and it should be visible before anyone reads a word of it. */}
          {shownLines.length > 0 ? (
            <div className="space-y-2.5">
              {shownLines.map((l, i) => {
                const id = l.orderItemId;
                const lots = lotsFor(id);
                const askLot = lots.length > 1;      // several lots delivered — only the raiser knows which one
                const knownLot = lots.length === 1 ? lots[0] : null; // already on record — stated, not asked
                const code = lineReason[id] ?? reasonCode;
                const meta = REASON_OPTIONS.find((o) => o.value === code);
                const seller = meta?.fault === 'seller';
                const qty = Number(qtys[id] ?? 0);
                return (
                  <div
                    key={id}
                    className={cn(
                      'overflow-hidden rounded-card border shadow-e1',
                      seller ? 'border-warning-500/30 bg-warning-500/6' : 'border-sky-500/25 bg-sky-500/5',
                    )}
                  >
                    <div className="flex items-start gap-2.5 border-b border-line/60 bg-surface/70 px-3 py-2.5">
                      <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-2xs font-semibold tabular-nums text-brand-700 dark:text-brand-300">{i + 1}</span>
                      {/* The pack shot. A return is an argument about physical goods — the person writing it is
                          looking at a carton, and a name alone does not settle which pack is in front of them. */}
                      <ProductThumb name={l.name} hue={accentForProductName(l.name)} size={34} />
                      <div className="min-w-0 flex-1">
                        <div className="type-body-strong wrap-break-word text-content">{l.name}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 type-caption text-muted">
                          <span>{fmtQty(l.delivered)} delivered</span>
                          {knownLot && <>· <CodeChip hue={entityHue(knownLot.batchNo)}>{knownLot.batchNo}</CodeChip></>}
                          {!knownLot && !lots.length && <span className="text-warning-600 dark:text-harvest-300">· no lot on record</span>}
                          {/* What this ONE product is worth — only when there are several. With a single
                              product its value IS the claim value in the band above, and printing it twice
                              invites the reader to wonder which of the two is the real one. */}
                          {multiProduct && qty > 0 && l.rate > 0 && <span className="text-content">· {inr2(qty * l.rate)}</span>}
                        </div>
                      </div>
                      <button
                        type="button" aria-label={`Remove ${l.name}`} title="Remove" onClick={() => removeProduct(id)}
                        className="inline-flex shrink-0 rounded p-1 text-muted transition-colors hover:bg-surface-2 hover:text-danger-600"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="grid gap-3 px-3 py-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block type-label text-muted">How many <span className="text-danger-500">*</span></label>
                        <Input
                          type="number" min={0} max={l.cap} size="sm" className="w-full text-right"
                          value={String(qtys[id] ?? 0)}
                          invalid={qty > l.cap}
                          onChange={(e) => setQty(id, Number(e.target.value), l.cap)}
                        />
                        <div className="mt-1 text-2xs text-subtle">up to {fmtQty(l.cap)} · {inr2(l.rate)} each</div>
                      </div>
                      {/* Asked ONLY when this product went out in more than one lot — otherwise the answer is
                          already on the dispatch record and is recorded from it. */}
                      {askLot && (
                        <div>
                          <label className="mb-1 block type-label text-muted">Which lot <span className="text-danger-500">*</span></label>
                          <ClaimBatchInput lots={lots} value={lineBatch[id] ?? ''} onChange={(v) => setLineBatch((m) => ({ ...m, [id]: v }))} />
                          <div className="mt-1 text-2xs text-subtle">{lots.length} lots were delivered — only you can say which is coming back.</div>
                        </div>
                      )}
                      {/* Asked ONLY on a multi-product return. With one product, the reason above IS its reason. */}
                      {multiProduct && (
                        <div className="sm:col-span-2">
                          <label className="mb-1 block type-label text-muted">What happened to this one <span className="text-danger-500">*</span></label>
                          <Select
                            value={code || undefined}
                            onChange={(v) => setProductReason(id, v)}
                            placeholder="Reason for this product"
                            className="w-full"
                            options={REASON_OPTIONS.map((o) => ({ value: o.value, label: <ReasonOptionLabel o={o} /> }))}
                          />
                          {meta && (
                            <div className={cn('mt-1 text-2xs', seller ? 'text-warning-700 dark:text-harvest-300' : 'text-muted')}>
                              {seller
                                ? 'Seller / carrier fault — normally credited in full once the goods are back.'
                                : 'Buyer’s choice — the credit follows the condition the goods come back in.'}
                            </div>
                          )}
                        </div>
                      )}
                      {multiProduct && (
                        <div className="sm:col-span-2">
                          <label className="mb-1 block type-label text-muted">Detail (optional)</label>
                          <Input
                            size="sm" maxLength={255} className="w-full"
                            value={lineNote[id] ?? ''}
                            onChange={(e) => setLineNote((m) => ({ ...m, [id]: e.target.value }))}
                            placeholder="e.g. 4 bottles leaked, outer carton soaked"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-control border border-dashed border-line px-3 py-4 text-center text-xs text-muted">
              Pick a product above to add it to the return.
            </div>
          )}

          {/* Proof (mandatory) */}
          <div>
            <label className="mb-1.5 block type-label text-content">
              Proof <span className="text-danger-500">*</span>
              <span className="ml-1 font-normal text-muted">— photos / PDF of the goods being returned</span>
            </label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            <div className="flex flex-wrap items-center gap-2">
              {proofs.map((u, i) => (
                <div key={`${u}-${i}`} className="relative">
                  <a href={u} target="_blank" rel="noreferrer" title="Open">
                    {isImage(u)
                      ? <img src={u} alt="proof" className="h-16 w-16 rounded-control border border-line object-cover" />
                      : <span className="flex h-16 w-16 items-center justify-center rounded-control border border-line bg-surface-2 text-muted"><FileText size={24} /></span>}
                  </a>
                  <button type="button" aria-label="Remove" onClick={() => setProofs((p) => p.filter((_, idx) => idx !== i))}
                    className="absolute -right-1.5 -top-1.5 inline-flex rounded-full bg-danger-500 p-0.5 text-white shadow-soft hover:bg-danger-600"><X size={12} /></button>
                </div>
              ))}
              {proofs.length < MAX_PROOFS && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading > 0}
                  className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-control border border-dashed border-line text-muted transition-colors hover:border-brand-400 hover:text-brand-600 disabled:opacity-50">
                  {uploading > 0 ? <Loader2 size={18} className="animate-spin" /> : <UploadIcon size={18} />}
                  <span className="text-2xs">{uploading > 0 ? 'Uploading' : 'Add'}</span>
                </button>
              )}
            </div>
            <div className="mt-1 text-2xs text-subtle">At least one photo/PDF is required · up to {MAX_PROOFS} · max 25 MB each.</div>
          </div>

          {/* Note */}
          <div>
            <label className="mb-1.5 block text-xs text-muted">Note (optional)</label>
            <Textarea rows={2} maxLength={255} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any detail for the approver / warehouse" />
          </div>
        </div>
      )}
    </Modal>
  );
}
